import React, { useEffect, useState, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getCurrentUser } from 'aws-amplify/auth';
import '../App.css';

const API_URL = 'https://opmlee572h.execute-api.us-west-2.amazonaws.com';

// ── Tiny QR code generator using Google Charts API ───────────────────────────
function QRCode({ url, size = 200 }) {
  const encoded = encodeURIComponent(url);
  const src = `https://api.qrserver.com/v1/create-qr-code/?size=${size}x${size}&data=${encoded}&bgcolor=f5f2eb&color=1a5c3a&margin=16`;
  return (
    <img src={src} alt="QR Code" width={size} height={size}
      style={{ borderRadius: '16px', border: '1px solid var(--border)', display: 'block' }}
    />
  );
}

function SharePage() {
  const { providerId: paramProviderId } = useParams();
  const navigate = useNavigate();
  const [provider,    setProvider]    = useState(null);
  const [userId,      setUserId]      = useState('');
  const [loading,     setLoading]     = useState(true);
  const [copied,      setCopied]      = useState(false);
  const [copiedCaption, setCopiedCaption] = useState('');
  const linkRef = useRef(null);

  const providerId = paramProviderId || userId;
  const bookingUrl = providerId ? `${window.location.origin}/provider/${providerId}` : '';

  useEffect(() => {
    getCurrentUser().then(u => setUserId(u.userId)).catch(() => {});
  }, []);

  useEffect(() => {
    if (!providerId) return;
    fetch(`${API_URL}/providers?providerId=${providerId}`)
      .then(r => r.json())
      .then(data => { setProvider(data); setLoading(false); })
      .catch(() => setLoading(false));
  }, [providerId]);

  const handleCopyLink = () => {
    navigator.clipboard.writeText(bookingUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 2500);
  };

  const handleCopyCaption = (text) => {
    navigator.clipboard.writeText(text);
    setCopiedCaption(text.slice(0, 20));
    setTimeout(() => setCopiedCaption(''), 2500);
  };

  const handleDownloadQR = () => {
    const encoded = encodeURIComponent(bookingUrl);
    const src = `https://api.qrserver.com/v1/create-qr-code/?size=800x800&data=${encoded}&bgcolor=f5f2eb&color=1a5c3a&margin=32`;
    const a = document.createElement('a');
    a.href = src;
    a.download = `vyblo-qr-${provider?.name || 'booking'}.png`;
    a.target = '_blank';
    a.click();
  };

  const name     = provider?.name || 'Your Name';
  const service  = provider?.services?.[0]?.name || 'Services';
  const location = provider?.location || '';
  const minPrice = provider?.services?.length > 0
    ? Math.min(...provider.services.map(s => parseFloat(s.price || 0)))
    : null;

  const captions = [
    {
      platform: 'Instagram',
      icon: '📸',
      text: `Book your next appointment with me online — no DMs needed! 🔗 ${bookingUrl}\n\n${location ? `📍 ${location}` : ''}${minPrice ? `\n💰 Starting at $${minPrice}` : ''}\n\n#vyblo #booking${service ? ` #${service.toLowerCase().replace(/\s/g, '')}` : ''}`,
    },
    {
      platform: 'TikTok',
      icon: '🎵',
      text: `Book me online now! Link in bio 🔗\n${bookingUrl}`,
    },
    {
      platform: 'Twitter / X',
      icon: '𝕏',
      text: `Now accepting online bookings! Book your ${service} appointment here 👇\n${bookingUrl}`,
    },
    {
      platform: 'Text / iMessage',
      icon: '💬',
      text: `Hey! You can book your appointment with me here: ${bookingUrl}`,
    },
  ];

  return (
    <div style={{ minHeight: '100vh', background: 'var(--cream)', fontFamily: 'DM Sans, sans-serif' }}>

      {/* Nav */}
      <nav style={{ background: 'var(--white)', borderBottom: '1px solid var(--border)', padding: '0 2rem', height: '60px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', position: 'sticky', top: 0, zIndex: 100 }}>
        <div style={{ fontFamily: 'Playfair Display, serif', fontSize: '1.25rem', fontWeight: 800, color: 'var(--green)', cursor: 'pointer' }} onClick={() => navigate('/dashboard')}>Vyblo</div>
        <button onClick={() => navigate('/dashboard')} style={{ background: 'var(--cream)', border: '1px solid var(--border)', borderRadius: '8px', padding: '0.5rem 1rem', cursor: 'pointer', fontSize: '0.82rem', fontWeight: 600, color: 'var(--text-mid)', fontFamily: 'DM Sans, sans-serif' }}>← Back to Dashboard</button>
      </nav>

      <div style={{ maxWidth: '900px', margin: '0 auto', padding: '2.5rem 1.5rem', display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>

        {/* Hero */}
        <div style={{ background: 'var(--green)', borderRadius: '20px', padding: '2.5rem', position: 'relative', overflow: 'hidden' }}>
          <div style={{ position: 'absolute', top: -60, right: -60, width: 240, height: 240, background: 'radial-gradient(circle, rgba(255,255,255,0.06) 0%, transparent 70%)', pointerEvents: 'none' }} />
          <div style={{ position: 'absolute', bottom: -40, left: -40, width: 160, height: 160, background: 'radial-gradient(circle, rgba(74,158,107,0.45) 0%, transparent 70%)', pointerEvents: 'none' }} />
          <div style={{ position: 'relative', zIndex: 1 }}>
            <div style={{ display: 'inline-flex', alignItems: 'center', gap: '0.4rem', background: 'rgba(255,255,255,0.15)', border: '1px solid rgba(255,255,255,0.25)', color: 'white', borderRadius: '50px', fontSize: '0.72rem', fontWeight: 700, padding: '0.25rem 0.875rem', marginBottom: '0.875rem', letterSpacing: '0.06em' }}>
              🔗 YOUR BOOKING LINK
            </div>
            <h1 style={{ fontFamily: 'Playfair Display, serif', fontSize: 'clamp(1.5rem, 3vw, 2rem)', fontWeight: 800, color: 'white', lineHeight: 1.15, marginBottom: '0.5rem' }}>
              Share your page, get booked
            </h1>
            <p style={{ color: 'rgba(255,255,255,0.65)', fontSize: '0.9rem', marginBottom: '1.5rem' }}>
              Post your link anywhere — Instagram bio, TikTok, texts — and clients can book you instantly.
            </p>
            {/* Link copy bar */}
            <div style={{ display: 'flex', gap: '0.625rem', alignItems: 'center' }}>
              <div style={{ flex: 1, background: 'rgba(255,255,255,0.12)', border: '1px solid rgba(255,255,255,0.2)', borderRadius: '10px', padding: '0.75rem 1rem', fontSize: '0.875rem', color: 'rgba(255,255,255,0.9)', fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {bookingUrl || 'Loading...'}
              </div>
              <button onClick={handleCopyLink}
                style={{ background: copied ? 'rgba(74,222,128,0.3)' : 'white', color: copied ? '#4ade80' : 'var(--green)', border: 'none', borderRadius: '10px', padding: '0.75rem 1.25rem', fontWeight: 700, fontSize: '0.875rem', cursor: 'pointer', fontFamily: 'DM Sans, sans-serif', flexShrink: 0, transition: 'all 0.2s', whiteSpace: 'nowrap' }}
              >{copied ? '✓ Copied!' : 'Copy Link'}</button>
            </div>
          </div>
        </div>

        {/* Two column layout */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.25rem', alignItems: 'start' }}>

          {/* QR Code card */}
          <div style={{ background: 'var(--white)', border: '1px solid var(--border)', borderRadius: '20px', padding: '2rem', boxShadow: 'var(--shadow-sm)', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '1.25rem' }}>
            <div>
              <p style={{ fontWeight: 700, fontSize: '0.875rem', color: 'var(--text)', marginBottom: '0.25rem', textAlign: 'center' }}>Your QR Code</p>
              <p style={{ fontSize: '0.78rem', color: 'var(--text-muted)', textAlign: 'center' }}>Clients scan this to book you instantly</p>
            </div>
            {bookingUrl ? (
              <QRCode url={bookingUrl} size={200} />
            ) : (
              <div style={{ width: 200, height: 200, background: 'var(--cream)', borderRadius: '16px', border: '1px solid var(--border)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--text-muted)', fontSize: '0.82rem' }}>Loading...</div>
            )}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', width: '100%' }}>
              <button onClick={handleDownloadQR}
                style={{ background: 'var(--green)', color: 'white', border: 'none', borderRadius: '10px', padding: '0.75rem', fontWeight: 700, fontSize: '0.875rem', cursor: 'pointer', fontFamily: 'DM Sans, sans-serif', width: '100%', transition: 'all 0.2s' }}
                onMouseEnter={e => e.currentTarget.style.background = 'var(--green-dark)'}
                onMouseLeave={e => e.currentTarget.style.background = 'var(--green)'}
              >⬇ Download QR Code</button>
              <p style={{ fontSize: '0.72rem', color: 'var(--text-muted)', textAlign: 'center', lineHeight: 1.5 }}>Print it and put it at your station, or add it to your Instagram story</p>
            </div>
          </div>

          {/* Stats + tips */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
            {/* Profile preview */}
            <div style={{ background: 'var(--white)', border: '1px solid var(--border)', borderRadius: '16px', padding: '1.5rem', boxShadow: 'var(--shadow-sm)' }}>
              <p style={{ fontSize: '0.72rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--text-muted)', marginBottom: '0.875rem' }}>What clients see</p>
              {loading ? (
                <div style={{ height: '60px', background: 'var(--cream)', borderRadius: '8px' }} />
              ) : (
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.875rem', padding: '0.875rem', background: 'var(--cream)', borderRadius: '12px' }}>
                  <div style={{ width: 48, height: 48, borderRadius: '50%', background: 'var(--green)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 800, fontSize: '1.1rem', color: 'white', flexShrink: 0, fontFamily: 'Playfair Display, serif', overflow: 'hidden' }}>
                    {provider?.profilePicUrl ? <img src={provider.profilePicUrl} alt={name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : name[0]?.toUpperCase()}
                  </div>
                  <div>
                    <p style={{ fontWeight: 700, fontSize: '0.9rem', color: 'var(--text)', marginBottom: '0.15rem' }}>{name}</p>
                    <p style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>
                      {provider?.services?.length || 0} service{provider?.services?.length !== 1 ? 's' : ''}
                      {minPrice ? ` · from $${minPrice}` : ''}
                      {location ? ` · ${location}` : ''}
                    </p>
                  </div>
                </div>
              )}
              <button onClick={() => navigate(`/provider/${providerId}`)} style={{ background: 'transparent', border: '1px solid var(--border)', borderRadius: '8px', padding: '0.5rem 1rem', color: 'var(--text-mid)', fontSize: '0.78rem', fontWeight: 600, cursor: 'pointer', fontFamily: 'DM Sans, sans-serif', marginTop: '0.875rem', width: '100%', transition: 'all 0.15s' }}
            onMouseEnter={e => { e.currentTarget.style.borderColor = 'rgba(26,92,58,0.3)'; e.currentTarget.style.color = 'var(--green)'; }}
            onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--border)'; e.currentTarget.style.color = 'var(--text-mid)'; }}
            >Preview your booking page →</button>
            </div>

            {/* Tips */}
            <div style={{ background: 'var(--cream-dark)', border: '1px solid var(--border)', borderRadius: '16px', padding: '1.5rem' }}>
              <p style={{ fontWeight: 700, fontSize: '0.875rem', color: 'var(--text)', marginBottom: '0.875rem' }}>💡 Tips to get more bookings</p>
              {[
                { icon: '📸', tip: 'Add the link to your Instagram bio — it\'s the #1 driver of bookings' },
                { icon: '🎵', tip: 'Put your QR code in TikTok videos — viewers can scan right from the screen' },
                { icon: '🖨️', tip: 'Print your QR code and put it at your station so walk-ins can rebook' },
                { icon: '💬', tip: 'Text your regulars directly — a personal message converts better than a post' },
              ].map((t, i) => (
                <div key={i} style={{ display: 'flex', gap: '0.625rem', marginBottom: i < 3 ? '0.625rem' : 0 }}>
                  <span style={{ flexShrink: 0 }}>{t.icon}</span>
                  <p style={{ fontSize: '0.78rem', color: 'var(--text-mid)', lineHeight: 1.6 }}>{t.tip}</p>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Caption templates */}
        <div style={{ background: 'var(--white)', border: '1px solid var(--border)', borderRadius: '20px', padding: '2rem', boxShadow: 'var(--shadow-sm)' }}>
          <div style={{ marginBottom: '1.5rem' }}>
            <h2 style={{ fontFamily: 'Playfair Display, serif', fontSize: '1.1rem', fontWeight: 800, color: 'var(--text)', marginBottom: '0.25rem' }}>Ready-to-post captions</h2>
            <p style={{ fontSize: '0.82rem', color: 'var(--text-muted)' }}>Copy and paste — your link is already included</p>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
            {captions.map((cap, i) => (
              <div key={i} style={{ border: '1px solid var(--border)', borderRadius: '14px', overflow: 'hidden' }}>
                <div style={{ background: 'var(--cream)', padding: '0.75rem 1.25rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid var(--border)' }}>
                  <span style={{ fontSize: '0.82rem', fontWeight: 700, color: 'var(--text)', display: 'flex', alignItems: 'center', gap: '0.4rem' }}>{cap.icon} {cap.platform}</span>
                  <button onClick={() => handleCopyCaption(cap.text)}
                    style={{ background: copiedCaption === cap.text.slice(0, 20) ? 'var(--green)' : 'var(--white)', color: copiedCaption === cap.text.slice(0, 20) ? 'white' : 'var(--text-mid)', border: '1px solid var(--border)', borderRadius: '7px', padding: '0.3rem 0.875rem', fontSize: '0.75rem', fontWeight: 600, cursor: 'pointer', fontFamily: 'DM Sans, sans-serif', transition: 'all 0.15s' }}
                  >{copiedCaption === cap.text.slice(0, 20) ? '✓ Copied!' : 'Copy'}</button>
                </div>
                <div style={{ padding: '1rem 1.25rem', background: 'var(--white)' }}>
                  <p style={{ fontSize: '0.82rem', color: 'var(--text-mid)', lineHeight: 1.7, whiteSpace: 'pre-wrap', margin: 0 }}>{cap.text}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

      </div>
    </div>
  );
}

export default SharePage;
